/**
 * Popover.tsx — wiederverwendbare Popover-Komponente.
 *
 * Liegt im Top-Layer (natives `popover`-Attribut, wo verfügbar) oder fällt auf
 * createPortal + position:fixed zurück. Positionierung bevorzugt CSS Anchor
 * Positioning, andernfalls getBoundingClientRect-Fallback mit Viewport-Guard.
 *
 * Bewusst KEINE Verdrahtung mit Anker-ARIA (aria-expanded, aria-haspopup) —
 * das übernimmt die aufrufende Seite (Paket G).
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { pushOverlay, peekTop } from '../../commands/overlayStack';
import styles from './Popover.module.css';

// ---------------------------------------------------------------------------
// Öffentliche API
// ---------------------------------------------------------------------------

export interface PopoverProps {
  /** Steuert Sichtbarkeit — true = offen, false = geschlossen/null. */
  open: boolean;
  /** Wird beim Schließen aufgerufen (Esc, Außen-Klick, toggle-Event). */
  onClose: () => void;
  /** Ref auf den Anker, an dem das Popover positioniert wird. */
  anchorRef: RefObject<HTMLElement>;
  /**
   * Gewünschte Platzierungsseite relativ zum Anker.
   * Logische Eigenschaften (writing-mode-unabhängig).
   * @default 'block-end'
   */
  placement?: 'block-end' | 'block-start' | 'inline-end' | 'inline-start';
  /**
   * ARIA-Rolle des Panels. Vorgabe 'dialog' (Bestandsverhalten). R2-10 setzt
   * 'presentation', wenn der Inhalt seine Semantik selbst mitbringt (eine
   * Listbox in einem Dialog waere fuer Screenreader eine Ebene zu viel).
   */
  role?: string;
  className?: string;
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------

/** Erkennt native Popover-API-Unterstützung. */
function supportsNativePopover(): boolean {
  if (typeof document === 'undefined') return false;
  const probe = document.createElement('div') as unknown as { showPopover?: unknown };
  return typeof probe.showPopover === 'function';
}

/** Erkennt CSS Anchor Positioning. */
function supportsAnchorPositioning(): boolean {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('anchor-name', '--x');
}

/** Throttle via requestAnimationFrame — gibt eine stabile Handler-Funktion zurück. */
function rafThrottle(fn: () => void): () => void {
  let raf: number | null = null;
  return () => {
    if (raf !== null) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      fn();
    });
  };
}

// Placement → CSS position-area-Wert (CSS Anchor Positioning Level 2 Syntax).
const PLACEMENT_TO_AREA: Record<NonNullable<PopoverProps['placement']>, string> = {
  'block-end': 'block-end center',
  'block-start': 'block-start center',
  'inline-end': 'center inline-end',
  'inline-start': 'center inline-start',
};

// ---------------------------------------------------------------------------
// Viewport-bewusste Fallback-Positionierung
// ---------------------------------------------------------------------------

interface FallbackPos {
  top: number;
  left: number;
}

/**
 * Berechnet `top`/`left` für das Popover anhand des Anker-Rects.
 * Klemmt an Viewport-Rändern, sodass das Panel nicht abgeschnitten wird.
 */
function computeFallbackPos(
  anchor: HTMLElement,
  popover: HTMLElement,
  placement: NonNullable<PopoverProps['placement']>,
): FallbackPos {
  const anchorRect = anchor.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MARGIN = 8; // Mindestabstand zum Viewport-Rand in px

  let top: number;
  let left: number;

  switch (placement) {
    case 'block-end':
      top = anchorRect.bottom;
      left = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2;
      break;
    case 'block-start':
      top = anchorRect.top - popoverRect.height;
      left = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2;
      break;
    case 'inline-end':
      top = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2;
      left = anchorRect.right;
      break;
    case 'inline-start':
      top = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2;
      left = anchorRect.left - popoverRect.width;
      break;
  }

  // Viewport-Guard: horizontal
  left = Math.max(MARGIN, Math.min(left, vw - popoverRect.width - MARGIN));
  // Viewport-Guard: vertikal
  top = Math.max(MARGIN, Math.min(top, vh - popoverRect.height - MARGIN));

  return { top, left };
}

// ---------------------------------------------------------------------------
// Feature-Flags (Modul-Scope — einmalig ermittelt, SSR-sicher da Browser-only)
// ---------------------------------------------------------------------------

const HAS_NATIVE_POPOVER = typeof document !== 'undefined' && supportsNativePopover();
const HAS_ANCHOR_POS = typeof document !== 'undefined' && supportsAnchorPositioning();

// CSS Anchor Positioning setzt nicht-standardisierte Eigenschaften (Draft-Spec).
// Wir csten bewusst auf Record<string,string> um CSSProperties-Strenge zu umgehen.
type AnchorStyle = Record<string, string>;

// ---------------------------------------------------------------------------
// Öffentliche Komponente (SSR-Wrapper)
// ---------------------------------------------------------------------------

export function Popover({
  open,
  onClose,
  anchorRef,
  placement = 'block-end',
  role = 'dialog',
  className,
  children,
}: PopoverProps): JSX.Element | null {
  // SSR-Schutz: kein Zugriff auf document während des Server-Renders.
  if (typeof document === 'undefined') return null;

  return (
    <PopoverInner
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      placement={placement}
      role={role}
      className={className}
    >
      {children}
    </PopoverInner>
  );
}

// ---------------------------------------------------------------------------
// Interne Implementierung (läuft nur im Browser)
// ---------------------------------------------------------------------------

function PopoverInner({
  open,
  onClose,
  anchorRef,
  placement,
  role,
  className,
  children,
}: Required<Omit<PopoverProps, 'className'>> & Pick<PopoverProps, 'className'>) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Stabil referenziertes onClose für Effekte ohne Re-Register bei jedem Render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Eindeutiger CSS-Custom-Property-Name für Anchor Positioning je Instanz.
  const anchorId = useId();
  const anchorName = `--popover-anchor-${anchorId.replace(/:/g, '')}`;

  // Fallback-Position (nur ohne Anchor-Positioning-Support).
  const [fallbackPos, setFallbackPos] = useState<FallbackPos | null>(null);

  // ---------------------------------------------------------------------------
  // Fallback-Positionierung: berechnen & bei resize/scroll nachführen
  // ---------------------------------------------------------------------------
  const updateFallbackPos = useCallback(() => {
    if (HAS_ANCHOR_POS) return;
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;
    setFallbackPos(computeFallbackPos(anchor, popover, placement));
  }, [anchorRef, placement]);

  // ---------------------------------------------------------------------------
  // Natives Popover öffnen / schließen
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const el = popoverRef.current;
    if (!el || !HAS_NATIVE_POPOVER) return;

    const nativeEl = el as HTMLElement & { showPopover(): void; hidePopover(): void };

    if (open) {
      try { nativeEl.showPopover(); } catch { /* bereits offen oder Element nicht im DOM */ }
    } else {
      try { nativeEl.hidePopover(); } catch { /* bereits geschlossen */ }
    }
  }, [open]);

  // ---------------------------------------------------------------------------
  // Anchor-Name auf den Anker setzen / beim Unmount aufräumen.
  // E4a-Verify-MAJOR: zusätzlich an `open` gekoppelt — anchorRef ist eine
  // STABILE Ref, deren .current der ContextMenu-Aufrufer beim Zeiger-Öffnen auf
  // den Punkt-Anker umbiegt. Ein reiner Mount-Effekt schrieb anchor-name nur auf
  // das ERSTE Element (den ⋯-Trigger) → auf Browsern mit CSS Anchor Positioning
  // erschien das Rechtsklick-/Long-Press-Menü am Button statt am Zeiger. Bei
  // jedem Öffnen das AKTUELLE Anker-Element markieren, das vorige aufräumen.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!HAS_ANCHOR_POS) return undefined;
    const anchor = anchorRef.current;
    if (!anchor) return undefined;

    anchor.style.setProperty('anchor-name', anchorName);
    return () => {
      anchor.style.removeProperty('anchor-name');
    };
  }, [anchorRef, anchorName, open]);

  // ---------------------------------------------------------------------------
  // Fallback-Positionierung: Initialposition + resize/scroll-Listener
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || HAS_ANCHOR_POS) return undefined;

    // Initialpositionierung nach einem Frame, damit das Popover im DOM ist.
    const frameId = requestAnimationFrame(() => updateFallbackPos());

    const throttled = rafThrottle(updateFallbackPos);
    window.addEventListener('resize', throttled, { passive: true });
    window.addEventListener('scroll', throttled, { passive: true, capture: true });

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', throttled);
      window.removeEventListener('scroll', throttled, { capture: true } as EventListenerOptions);
    };
  }, [open, updateFallbackPos]);

  // ---------------------------------------------------------------------------
  // Light-Dismiss: Klick außerhalb + Esc + Fokus-Rückkehr zum Anker
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return undefined;

    // Zentrale LIFO-Overlay-Verwaltung: solange offen, liegt dieses Popover als
    // oberste Ebene auf dem overlayStack. Damit schliesst die Hardware-Back-Taste
    // (ueber den zentral angedockten Handler) genau dieses Popover, auch wenn es
    // ueber einem Dialog liegt (cross-type-Semantik).
    const entry = {
      id: anchorId,
      close: () => {
        anchorRef.current?.focus({ preventScroll: true });
        onCloseRef.current();
      },
    };
    const unregister = pushOverlay(entry);

    const handlePointerDown = (event: PointerEvent) => {
      const popover = popoverRef.current;
      const anchor = anchorRef.current;
      if (!popover) return;
      // Klick innerhalb des Popovers oder auf den Anker → kein Schließen.
      if (popover.contains(event.target as Node)) return;
      if (anchor?.contains(event.target as Node)) return;
      onCloseRef.current();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Nur schliessen, wenn dieses Popover die OBERSTE Overlay-Ebene ist —
      // sonst darf ein darunterliegendes Popover/Dialog nicht mitschliessen.
      if (peekTop() !== entry) return;
      event.preventDefault();
      anchorRef.current?.focus({ preventScroll: true }); // Fokus zurück zum Anker
      onCloseRef.current();
    };

    // `toggle`-Event des nativen Popovers: synchronisiert den React-State, wenn
    // der Browser das Popover (z.B. bei popover="auto") selbst schließt.
    const handleToggle = (event: Event) => {
      const te = event as ToggleEvent;
      if (te.newState === 'closed') {
        anchorRef.current?.focus({ preventScroll: true });
        onCloseRef.current();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    document.addEventListener('keydown', handleKeyDown);

    const el = popoverRef.current;
    if (el && HAS_NATIVE_POPOVER) {
      el.addEventListener('toggle', handleToggle);
    }

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      document.removeEventListener('keydown', handleKeyDown);
      el?.removeEventListener('toggle', handleToggle);
      unregister();
    };
  }, [open, anchorRef, anchorId]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Gibt null zurück wenn geschlossen — kein DOM-Overhead wenn nicht sichtbar.
  if (!open) return null;

  // Inline-Style je nach verfügbarer Positionierungs-API.
  const posStyle: CSSProperties | AnchorStyle = HAS_ANCHOR_POS
    ? ({ positionAnchor: anchorName, positionArea: PLACEMENT_TO_AREA[placement] } satisfies AnchorStyle)
    : fallbackPos
      ? ({ top: fallbackPos.top, left: fallbackPos.left } satisfies CSSProperties)
      : ({ visibility: 'hidden' } satisfies CSSProperties); // unsichtbar bis erste Berechnung

  if (HAS_NATIVE_POPOVER) {
    // Native Top-Layer: kein Portal nötig. Der Browser hebt das Element automatisch
    // über alle gestapelten Inhalte (Top-Layer ≙ kein z-index nötig).
    return (
      <div
        // `popover` ist ein gültiges HTML-Attribut (Popover API), aber noch nicht
        // in allen TypeScript-DOM-Definitionen als React-Prop registriert.
        {...{ popover: 'manual' }}
        ref={popoverRef}
        role={role}
        className={`${styles.popover}${className ? ` ${className}` : ''}`}
        style={posStyle as CSSProperties}
        data-placement={placement}
      >
        {children}
      </div>
    );
  }

  // Fallback: Portal nach document.body mit position:fixed + z-index-Token.
  return createPortal(
    <div
      ref={popoverRef}
      role={role}
      className={`${styles.popover} ${styles.popoverFallback}${className ? ` ${className}` : ''}`}
      style={posStyle as CSSProperties}
      data-placement={placement}
    >
      {children}
    </div>,
    document.body,
  );
}
